/**
 * Local-storage key that toggles upload debug logging. Logging is OFF in
 * production by default; flip it on in any environment from the dev console:
 *
 * ```
 * localStorage.uploadDebug = '1'; // enable
 * localStorage.uploadDebug = '';  // disable
 * ```
 *
 * Mirrors the `analyticsDebug` / player debug-key convention used elsewhere.
 */
export const UPLOAD_DEBUG_KEY = 'uploadDebug';

const BYTES_PER_MB = 1_000_000;

const formatSpeed = (bytesPerSecond: number) => `${(bytesPerSecond / BYTES_PER_MB).toFixed(2)} MB/s`;
const formatBytes = (bytes: number) => `${(bytes / BYTES_PER_MB).toFixed(2)} MB`;
const formatQueuedSizes = (sizes: number[]) => (sizes.length ? sizes.map(formatBytes).join(', ') : 'none');

/**
 * Centralised debug logger for the upload pipeline. Every upload log lives here
 * as a method so call sites stay terse and the whole feature's logging can be
 * toggled from one place (see {@link UPLOAD_DEBUG_KEY}) rather than scattering
 * `console` calls — and gating them — across hooks in three packages.
 */
class UploadLogger {
  private get isEnabled() {
    return typeof window !== 'undefined' && !!window.localStorage && !!window.localStorage[UPLOAD_DEBUG_KEY];
  }

  private log(message: string) {
    if (!this.isEnabled) {
      return;
    }

    console.info(
      '%cUpload',
      'font-weight: bold; background-color: #4030FF; color: #FFFFFF; padding: 2px 8px; border-radius: 4px;',
      message,
    );
  }

  /** Adaptive concurrency initialised on mount. */
  concurrencyInit({ workers }: { workers: number }) {
    this.log(`init — ${workers} workers | reason: instability-triggered adaptive enabled — starting fixed`);
  }

  /** Upload instability switched the scheduler from fixed to adaptive mode. */
  concurrencyUploadInstabilityDetected({ workers }: { workers: number }) {
    this.log(`upload instability detected → adaptive mode | reset to ${workers} worker`);
  }

  /** One adaptive tick: reports the chosen concurrency and the budget that drove it. */
  concurrencyAdjusted({
    from,
    to,
    activeWorkers,
    speed,
    inFlightBytes,
    budget,
    queuedUploadSizes,
  }: {
    from: number;
    to: number;
    activeWorkers: number;
    speed: number | null;
    inFlightBytes: number;
    budget: number;
    queuedUploadSizes: number[];
  }) {
    const direction = to > from ? '↑' : '↓';
    const summary = from === to ? `keep ${from}` : `${from} → ${to} ${direction}`;
    const speedStr = speed ? formatSpeed(speed) : '?';
    const budgetStr = speed
      ? `budget: ${formatBytes(budget)} | in-flight: ${formatBytes(inFlightBytes)} | free: ${formatBytes(budget - inFlightBytes)}`
      : `speed unknown | queued sizes: ${formatQueuedSizes(queuedUploadSizes)}`;

    this.log(`${summary} | active: ${activeWorkers} | speed: ${speedStr} | ${budgetStr}`);
  }

  /** Adaptive concurrency recovered to MAX, so the scheduler reverted to fixed mode. */
  concurrencyRevertedToFixed() {
    this.log('concurrency reached MAX → reverting to fixed mode');
  }

  /** A multipart chunk started transferring. */
  chunkStarted({ partNumber }: { partNumber: number }) {
    this.log(`chunk started | partNumber: ${partNumber}`);
  }

  /** A multipart chunk finished transferring. */
  chunkCompleted({ partNumber, eTag }: { partNumber: number; eTag?: string }) {
    this.log(`chunk completed | partNumber: ${partNumber} | ETag: ${eTag ?? 'none'}`);
  }

  /** A PUT retry switched from the primary presigned URL to fallbackUrl. */
  fallbackUrlSelected({ partNumber, uploadId }: { partNumber?: number; uploadId: string }) {
    this.log(
      `fallback URL selected | uploadId: ${uploadId}${partNumber === undefined ? '' : ` | partNumber: ${partNumber}`}`,
    );
  }

  /** Speed sampling stopped because the user is no longer uploading. */
  speedCheckStopped() {
    this.log('user is no longer uploading — stopping speed check');
  }
}

/** Singleton upload logger. Call its methods from hooks; toggle via {@link UPLOAD_DEBUG_KEY}. */
export const uploadLogger = new UploadLogger();
